// App.js
import React, {useContext, useState, useEffect} from 'react';
import {Routes, Route, BrowserRouter, useLocation} from 'react-router-dom';
import PropTypes from 'prop-types';
import {ReactKeycloakProvider} from '@react-keycloak/web';
import {ThemeProvider} from '@mui/material/styles';
import {CssBaseline} from '@mui/material';
import theme from './theme';
import {ConfigContext} from './ConfigProvider';
import {createKeycloak} from './pages/keycloak/keycloak';
import Header from './components/Header';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import TosConsentGate from './components/TosConsentGate';
import Unavailable from './components/Unavailable';
import Home from './pages/home';
import SearchDetails from './pages/SearchDetails';
import PrivateRoute from './pages/keycloak/privateRoute';
import SecuredPage from './pages/keycloak/securedPage';
import LoadingSpinner from './components/LoadingSpinner';
import Login from './pages/login';
import Profile from './pages/profile/profile';
import UserProfile from './pages/userProfile';
import BookAppointment from './pages/bookAppointment';
import Privacy from './pages/privacy';
import Terms from './pages/terms';
import RegistrationWizard from './pages/registration/RegistrationWizard';
import BusinessTermsPage from './pages/businessTerms';
import {Box} from "@mui/material";
import Register from "./pages/register";
import Unsubscribe from "./pages/unsubscribe"
import Subscription from "./pages/subscription"
import Faq from "./pages/faq"
import Support from "./pages/support"
import Version from './pages/version';
import Aboutus from './pages/aboutus';
import PlannerStep1 from "./pages/planner/plannerstep1";
import PlannerStep2 from "./pages/planner/plannerstep2";
import TelemetryTracker from './components/TelemetryTracker';
import Appointments from "./pages/appointments";
import PurchaseHistory from "./pages/purchaseHistory";

// Inner boundary for page content: keeps Header/Footer visible on a page-level
// error and resets when the route changes so navigating away recovers.
function ContentErrorBoundary({ children }) {
    const location = useLocation();
    return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

ContentErrorBoundary.propTypes = {
    children: PropTypes.node,
};

function App() {
    // 1) Get the config from the provider context
    const config = useContext(ConfigContext);

    // 2) Set up keycloak
    const [keycloak, setKeycloak] = useState(null);

    useEffect(() => {
        if (config) {
            // As soon as we have config, create Keycloak
            setKeycloak(createKeycloak(config));
        }
    }, [config]);

    // 3) If Keycloak isn't yet created, show a loading spinner
    //    (ensures we have config -> keycloak -> THEN show the app)
    if (!keycloak) {
        // In unit tests you might want to handle this differently
        // but typically you'd show a spinner or loader
        return <Box style={{height: "100vh", width: "100vw", alignContent: "center"}}>
            <LoadingSpinner statusMessage={"Loading"}/>
        </Box>;
    }

    // 4) Otherwise, render your application
    return (
        <ReactKeycloakProvider authClient={keycloak}>
            <ThemeProvider theme={theme}>
                <CssBaseline>
                    <ErrorBoundary>
                        <BrowserRouter>
                            <TosConsentGate/>
                            <TelemetryTracker/>
                            <Header/>
                            <ContentErrorBoundary>
                                <Routes>
                                <Route exact path="/" element={<Home/>}/>
                                <Route path="/unavailable" element={<Unavailable/>}/>
                                <Route path="/details" element={<SearchDetails/>}/>
                                <Route path="/privacy" element={<Privacy/>}/>
                                <Route path="/business-terms" element={<BusinessTermsPage/>}/>
                                <Route path="/terms" element={<Terms/>}/>
                                <Route path="/login" element={<Login/>}/>
                                <Route
                                    path="/registration"
                                    element={
                                        <PrivateRoute>
                                            <RegistrationWizard/>
                                        </PrivateRoute>
                                    }
                                />
                                <Route path="/unsubscribe" element={<Unsubscribe/>}/>
                                <Route path="/subscription" element={<Subscription/>}/>
                                <Route path="/faq" element={<Faq/>}/>
                                <Route path="/support" element={<Support/>}/>
                                <Route path="/version-details" element={<Version/>}/>
                                <Route path="/aboutus" element={<Aboutus/>}/>
                                <Route path="/plannerstep1" element={<PlannerStep1/>}/>
                                <Route path="/plannerstep2" element={<PlannerStep2/>}/>

                                <Route
                                    path="/profile"
                                    element={
                                        <PrivateRoute>
                                            <Profile/>
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/userProfile"
                                    element={
                                        <PrivateRoute>
                                            <UserProfile/>
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/register"
                                    element={
                                        <PrivateRoute>
                                            <Register/>
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/bookAppointment"
                                    element={
                                        <PrivateRoute>
                                            <BookAppointment/>
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/secured"
                                    element={
                                        <PrivateRoute>
                                            <SecuredPage/>
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/appointments"
                                    element={
                                        <PrivateRoute>
                                            <Appointments/>
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/purchaseHistory"
                                    element={
                                        <PrivateRoute>
                                            <PurchaseHistory/>
                                        </PrivateRoute>
                                    }
                                />

                                <Route path="/:friendlyUrl" element={<SearchDetails/>}/>
                                </Routes>
                            </ContentErrorBoundary>
                            <Footer/>
                        </BrowserRouter>
                    </ErrorBoundary>
                </CssBaseline>
            </ThemeProvider>
        </ReactKeycloakProvider>
    );
}

export default App;
